import React, { useState, useEffect, useCallback } from 'react';
import { priceBookAPI } from '../lib/api';
import { AppDrawer } from '../components/ui/AppDrawer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, Loader2, BookOpen, Plus, Layers } from 'lucide-react';
import { toast } from 'sonner';

const TYPE_COLORS = {
  service: 'bg-blue-100 text-blue-700',
  material: 'bg-amber-100 text-amber-700',
  labor: 'bg-purple-100 text-purple-700',
  bundle: 'bg-green-100 text-green-700',
};

export function PriceBookPicker({ open, onOpenChange, onAdd, targetType = 'invoice', targetId, optionId }) {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [adding, setAdding] = useState(null);

  const fetchData = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const [catRes, itemRes] = await Promise.all([
        priceBookAPI.listCategories(),
        priceBookAPI.listItems({
          search: search || undefined,
          category_id: catFilter || undefined,
          item_type: typeFilter || undefined,
          page_size: 50,
        }),
      ]);
      setCategories(catRes.data.categories || []);
      setItems(itemRes.data.items || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [open, search, catFilter, typeFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAdd = async (item) => {
    setAdding(item.id);
    try {
      let res;
      if (targetType === 'estimate') {
        res = await priceBookAPI.addToEstimate(item.id, targetId, { option_id: optionId, quantity: 1 });
      } else {
        res = await priceBookAPI.addToInvoice(item.id, targetId, { quantity: 1 });
      }
      const count = res.data.items_added;
      toast.success(`Added ${count} line item${count > 1 ? 's' : ''} from "${item.name}"`);
      if (onAdd) onAdd(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add item');
    } finally {
      setAdding(null);
    }
  };

  return (
    <AppDrawer
      open={open}
      onOpenChange={onOpenChange}
      title="Add from Price Book"
      description="Select items to add as line items. Bundles auto-expand into components."
      footer={<Button variant="outline" onClick={() => onOpenChange(false)}>Done</Button>}
    >
      <div className="space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="pl-9 h-9" data-testid="pb-picker-search" />
          </div>
          <Select value={typeFilter || 'all'} onValueChange={v => setTypeFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-[120px] h-9"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="service">Service</SelectItem>
              <SelectItem value="material">Material</SelectItem>
              <SelectItem value="labor">Labor</SelectItem>
              <SelectItem value="bundle">Bundle</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {categories.length > 0 && (
          <div className="flex gap-1 flex-wrap">
            <Button variant={!catFilter ? 'default' : 'outline'} size="sm" className="h-7 text-xs" onClick={() => setCatFilter('')}>All</Button>
            {categories.map(c => (
              <Button key={c.id} variant={catFilter === c.id ? 'default' : 'outline'} size="sm" className="h-7 text-xs" onClick={() => setCatFilter(catFilter === c.id ? '' : c.id)}>
                {c.name}
              </Button>
            ))}
          </div>
        )}
        <div className="space-y-1 max-h-[400px] overflow-y-auto">
          {loading ? (
            <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No items found</p>
            </div>
          ) : items.map(item => (
            <div key={item.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg border hover:bg-muted/50 transition-colors" data-testid={`pb-pick-${item.id}`}>
              <div className="w-10 h-10 rounded border bg-muted flex items-center justify-center overflow-hidden shrink-0">
                {item.image_url ? <img src={item.image_url} alt="" className="w-full h-full object-cover" /> : <BookOpen className="w-4 h-4 text-muted-foreground/40" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">{item.name}</span>
                  <Badge className={`${TYPE_COLORS[item.item_type]} text-[9px] capitalize`}>{item.item_type}</Badge>
                  {item.pricing_mode === 'bundle' && <Badge variant="outline" className="text-[9px]"><Layers className="w-2.5 h-2.5 mr-0.5 inline" />{item.components?.length || 0}</Badge>}
                </div>
                {item.description && <p className="text-xs text-muted-foreground mt-0.5 truncate">{item.description}</p>}
                {item.pricing_mode === 'bundle' && item.components?.length > 0 && (
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {item.components.map(c => `${c.component_name} x${c.quantity}`).join(', ')}
                  </p>
                )}
              </div>
              <span className="text-sm font-semibold whitespace-nowrap">${item.calculated_price.toFixed(2)}</span>
              <Button size="sm" variant="outline" className="h-8 shrink-0" onClick={() => handleAdd(item)} disabled={adding === item.id} data-testid={`pb-add-${item.id}`}>
                {adding === item.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </AppDrawer>
  );
}
